import { makeStyles } from 'tss-react/mui';
import { spacing } from '../../styles/jss/variables';
import { getBadgeTextFontSize, getBadgeTextSpacingOverride } from '../../styles/jss/badgeTextTypography';
// Width constants for FF Rounded badge
const FF_ROUNDED_WIDTHS = {
    mobile: '104px',
    tablet: '180px',
    desktop: '240px',
};
// Text truncation styles - used across multiple badge text classes
const TEXT_TRUNCATION_STYLES = {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 4,
    WebkitBoxOrient: 'vertical',
};
// Common FF Rounded Badge base styles
const createFFRoundedBadgeBaseStyles = (themeSpacing) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `20px ${themeSpacing.xs}`,
    maskImage: 'url("/images/roundels/roundel-1.svg")',
    WebkitMaskImage: 'url("/images/roundels/roundel-1.svg")',
    maskSize: 'contain',
    WebkitMaskSize: 'contain',
    maskRepeat: 'no-repeat',
    WebkitMaskRepeat: 'no-repeat',
    textAlign: 'center',
});
const useStyles = makeStyles()((theme, params) => {
    // text/textProductCard are pinned to their own branch regardless of the actual prop values,
    // so each class's typography can never drift from PIMBadge's textLineContent
    const detailParams = Object.assign(Object.assign({}, params), { isFromProductCard: false });
    const cardParams = { isFromProductCard: true };
    return {
        container: {
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
            // alignItems and top/bottom padding are set solely by the topLeft/topRight/bottomLeft/bottomRight
            // classes below - declaring them here too would create two rules for the same property on the
            // same element, with the winner decided by CSS injection order (non-deterministic)
            justifyContent: 'flex-start',
            overflow: 'hidden',
            paddingLeft: 0,
            paddingRight: 0,
            margin: 0,
            cursor: 'auto',
        },
        topLeft: {
            top: 0,
            left: 0,
            right: 'auto',
            bottom: 'auto',
            alignItems: 'flex-start',
            paddingTop: spacing.xs,
        },
        topRight: {
            top: 0,
            right: 0,
            left: 'auto',
            bottom: 'auto',
            alignItems: 'flex-end',
            paddingTop: spacing.xs,
        },
        bottomLeft: {
            bottom: 0,
            left: 0,
            top: 'auto',
            right: 'auto',
            alignItems: 'flex-start',
            paddingBottom: spacing.xs,
        },
        bottomRight: {
            bottom: 0,
            right: 0,
            top: 'auto',
            left: 'auto',
            alignItems: 'flex-end',
            paddingBottom: spacing.xs,
        },
        carouselPaddingLeft: {
            paddingLeft: spacing.xs,
        },
        carouselPaddingRight: {
            paddingRight: spacing.xs,
        },
        imageOnly: {
            // Image-only badges have responsive width for ProductDetailImages
            width: '100px',
            [theme.breakpoints.up('sm')]: {
                width: '140px',
            },
            [theme.breakpoints.up('md')]: {
                width: '100px',
            },
            [theme.breakpoints.up('lg')]: {
                width: '140px',
            },
        },
        imageOnlyProductCard: {
            // Image-only badges for product cards
            width: '100px',
        },
        // Text wrapper and text styles for text-only variant
        // Text wrapper now sizes naturally without fixed width
        textWrapper: {
            width: 'fit-content',
            maxWidth: '100%',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'center',
            minHeight: 'auto',
        },
        text: Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, theme.typography.xxs_bold), { fontSize: getBadgeTextFontSize(detailParams, 'base') }), getBadgeTextSpacingOverride(detailParams, 'base')), TEXT_TRUNCATION_STYLES), { [theme.breakpoints.up('sm')]: Object.assign(Object.assign(Object.assign({}, theme.typography.small_bold), { fontSize: getBadgeTextFontSize(detailParams, 'sm') }), getBadgeTextSpacingOverride(detailParams, 'sm')), [theme.breakpoints.up('md')]: Object.assign(Object.assign(Object.assign({}, theme.typography.xxs_bold), { fontSize: getBadgeTextFontSize(detailParams, 'md') }), getBadgeTextSpacingOverride(detailParams, 'md')), [theme.breakpoints.up('lg')]: Object.assign(Object.assign(Object.assign({}, theme.typography.small_bold), { fontSize: getBadgeTextFontSize(detailParams, 'lg') }), getBadgeTextSpacingOverride(detailParams, 'lg')), [theme.breakpoints.up('xl')]: Object.assign({ fontSize: getBadgeTextFontSize(detailParams, 'xl') }, getBadgeTextSpacingOverride(detailParams, 'xl')), padding: `0 ${spacing.xs}` }),
        textProductCard: Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, theme.typography.xxs_bold), { fontSize: getBadgeTextFontSize(cardParams, 'base') }), getBadgeTextSpacingOverride(cardParams, 'base')), { padding: `0 ${spacing.xs}` }), TEXT_TRUNCATION_STYLES),
        // Image styles - container that wraps the actual image element
        imageWrapper: {
            // Base width: 100px - acts as container for the image
            // Applied by imageWrapperResponsive or stays 100px for product cards
            width: '100px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            backgroundColor: 'transparent',
            padding: 0,
        },
        // Responsive widths for ProductDetailImages badges (overrides base imageWrapper width)
        imageWrapperResponsive: {
            width: '100px',
            [theme.breakpoints.up('sm')]: {
                width: '140px',
            },
            [theme.breakpoints.up('md')]: {
                width: '100px',
            },
            [theme.breakpoints.up('lg')]: {
                width: '140px',
            },
        },
        imageWrapperProductCard: {
            width: '72px',
        },
        productDetailImageWrapper: {
            [theme.breakpoints.up('md')]: {
                aspectRatio: '1 / 1',
            },
        },
        image: {
            width: '100%',
            height: 'auto',
            display: 'block',
            objectFit: 'cover',
            backgroundColor: 'transparent',
            padding: '0 !important', // Override default padding from react-multi-carousel library when images are used inside carousel (e.g., in ProductDetailImages)
        },
        imageErrorFallback: Object.assign(Object.assign(Object.assign({ width: '100%', padding: `0 ${spacing.xs}` }, theme.typography.xxs_bold), TEXT_TRUNCATION_STYLES), { textAlign: 'left', [theme.breakpoints.up('md')]: Object.assign({}, theme.typography.small_bold) }),
        // Flying Flowers Roundel Badge
        ffRoundedContainer: {
            width: FF_ROUNDED_WIDTHS.mobile,
            height: FF_ROUNDED_WIDTHS.mobile,
            margin: `${spacing.xxs} ${spacing.xs}`,
            [theme.breakpoints.up('sm')]: {
                width: FF_ROUNDED_WIDTHS.tablet,
                height: FF_ROUNDED_WIDTHS.tablet,
            },
            [theme.breakpoints.up('md')]: {
                width: FF_ROUNDED_WIDTHS.desktop,
                height: FF_ROUNDED_WIDTHS.desktop,
            },
        },
        ffRoundedContainerProductCard: {
            width: FF_ROUNDED_WIDTHS.mobile,
            height: FF_ROUNDED_WIDTHS.mobile,
            margin: `${spacing.xxs} ${spacing.xs}`,
        },
        ffRoundedBadge: Object.assign(Object.assign({}, createFFRoundedBadgeBaseStyles(spacing)), { [theme.breakpoints.up('sm')]: {
                padding: `24px ${spacing.xs}`,
            }, [theme.breakpoints.up('md')]: {
                padding: `56px ${spacing.medium}`,
            } }),
        ffRoundedBadgeProductCard: Object.assign(Object.assign({}, createFFRoundedBadgeBaseStyles(spacing)), { [theme.breakpoints.up('sm')]: {
                padding: `24px ${spacing.xs}`,
            } }),
        ffRoundedText: Object.assign(Object.assign(Object.assign({}, theme.typography.xxs_bold), TEXT_TRUNCATION_STYLES), { textAlign: 'center', textOverflow: 'clip', width: '88px', [theme.breakpoints.up('sm')]: Object.assign(Object.assign({}, theme.typography.large_bold), { lineHeight: '1.5rem', width: '144px', WebkitLineClamp: 4 }), [theme.breakpoints.up('md')]: Object.assign(Object.assign({}, theme.typography.xl_bold), { width: '192px', WebkitLineClamp: 4 }), paddingBottom: 0 }),
        ffRoundedTextProductCard: Object.assign(Object.assign(Object.assign({}, theme.typography.xxs_bold), TEXT_TRUNCATION_STYLES), { textAlign: 'center', textOverflow: 'clip', paddingBottom: 0, width: '88px' }),
        ffSquareText: Object.assign(Object.assign(Object.assign(Object.assign({}, theme.typography.xxs_bold), { [theme.breakpoints.up('sm')]: Object.assign({}, theme.typography.small_bold), [theme.breakpoints.up('md')]: Object.assign({}, theme.typography.xxs_bold), [theme.breakpoints.up('lg')]: Object.assign({}, theme.typography.small_bold) }), TEXT_TRUNCATION_STYLES), { textAlign: 'left', padding: `0 ${spacing.xs}` }),
        ffSquareTextProductCard: Object.assign(Object.assign(Object.assign({}, theme.typography.xxs_bold), { padding: `0 ${spacing.xs}` }), TEXT_TRUNCATION_STYLES),
    };
});
export default useStyles;
